/**
 * ResidentAccessHero — Weidner Via6 page hero for /resident-access.
 * See `FloorPlansHero.tsx` and `_PageImageHeroBody.tsx` for the shared
 * pattern. Owns its slot registration, contentSchema, and hidden
 * literal-path anchors; visual rendering is delegated to the body.
 */

import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { PageImageHeroBody } from './_PageImageHeroBody';

const bgDef = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Resident access hero image',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

export function ResidentAccessHero() {
  return (
    <>
      <PageImageHeroBody
        titlePath="residentaccesshero.title"
        bgPath="residentaccesshero.bg_image"
        bgDef={bgDef}
        defaultTitle="Resident Access"
        keywords={['lobby', 'entrance', 'residents', 'interior']}
        pageHint="residentaccesshero"
        altText="Resident access hero image"
      />

      <RichTextContent
        path="residentaccesshero.title"
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <EditableSlotGroup
        groupPath="residentaccesshero.bg_image"
        definition={bgDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ResidentAccessHero',
  displayName: 'Resident Access Hero',
  tokens: {
    'residentaccesshero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: 'Resident Access',
    },
  },
  slotGroups: {
    'residentaccesshero.bg_image': {
      label: 'Background Image',
      fields: {
        image: {
          label: 'Image',
          type: 'image',
          defaultField: 'property_featured_image',
        },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultValue: 'Resident access hero image',
        },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 0,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
    },
  },
};

(ResidentAccessHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ResidentAccessHero;
